import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { api } from '../lib/api.js';
import '../styles/dashboard.css';

/**
 * DashboardShell — persistent layout for the logged-in app.
 *
 * - Top bar: brand, tenant chip, user email, logout
 * - Left nav (collapsible on mobile): Staff / Scans / Branding / Settings
 * - Trial banner (only shows if tenant has an active trial)
 * - <Outlet /> renders the active page
 *
 * Uses React Router v6's nested routes — parent route renders this
 * shell, child routes render inside the <Outlet />.
 */

const NAV_ITEMS = [
  {
    to: '/dashboard/staff',
    label: 'Staff',
    icon: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c1-4 4-6 8-6s7 2 8 6" />
      </>
    ),
  },
  {
    to: '/dashboard/scans',
    label: 'Scans',
    icon: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M14 14h7v7" />
      </>
    ),
  },
  {
    to: '/dashboard/branding',
    label: 'Branding',
    icon: (
      <>
        <path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z" />
      </>
    ),
  },
  {
    to: '/dashboard/settings',
    label: 'Settings',
    icon: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2-1.2l-.4-2.4h-4l-.4 2.4a7 7 0 0 0-2 1.2l-2.3-.9-2 3.4 2 1.5a7 7 0 0 0 0 2.4l-2 1.5 2 3.4 2.3-.9a7 7 0 0 0 2 1.2l.4 2.4h4l.4-2.4a7 7 0 0 0 2-1.2l2.3.9 2-3.4-2-1.5c0-.4.1-.8.1-1.2z" />
      </>
    ),
  },
];

export function DashboardShell() {
  const { admin, tenant, refresh } = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Continue even if the API call fails — local-clear the session
    }
    await refresh();
    navigate('/login', { replace: true });
  }

  return (
    <div className="dashboard">
      <header className="dashboard-topbar">
        <div className="dashboard-topbar-inner">
          <div className="dashboard-identity">
            <Link to="/dashboard" className="brand-lockup" aria-label="TocynID dashboard">
              <span className="brand-tocyn">Tocyn</span>
              <span className="brand-id">ID</span>
            </Link>
            {tenant?.name && (
              <span className="dashboard-tenant-chip" title={tenant.name}>
                <strong>{tenant.name}</strong>
              </span>
            )}
          </div>

          <div className="dashboard-user">
            <span className="muted" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {admin?.email}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 'var(--fs-sm)', padding: '0.4rem 0.9rem' }}
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut ? 'Logging out…' : 'Log out'}
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-body">
        <nav className="dashboard-nav" aria-label="Main">
          <ul className="dashboard-nav-list">
            {NAV_ITEMS.map(item => (
              <li key={item.to} className="dashboard-nav-item">
                <NavLink
                  to={item.to}
                  className={({ isActive }) => `dashboard-nav-link ${isActive ? 'active' : ''}`}
                >
                  <svg className="dashboard-nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                    {item.icon}
                  </svg>
                  <span>{item.label}</span>
                  {item.comingSoon && (
                    <span className="dashboard-nav-badge" aria-label="Coming soon">Soon</span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="dashboard-main">
          {tenant?.trial && <TrialBanner trial={tenant.trial} />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */

function TrialBanner({ trial }) {
  const { daysLeft, expired } = trial;

  if (expired) {
    return (
      <div className="trial-banner trial-banner-expired" role="status">
        <div className="trial-banner-content">
          <strong>Your trial has ended.</strong>{' '}
          Add a subscription to keep your verify pages live.
        </div>
        <button type="button" className="btn btn-secondary" disabled title="Billing flow ships in Phase 3">
          Subscribe (coming soon)
        </button>
      </div>
    );
  }

  const cls = daysLeft <= 3
    ? 'trial-banner-urgent'
    : daysLeft <= 7
      ? 'trial-banner-warn'
      : 'trial-banner-ok';

  return (
    <div className={`trial-banner ${cls}`} role="status">
      <div className="trial-banner-content">
        <strong>
          {daysLeft === 1 ? '1 day' : `${daysLeft} days`} left in your free trial.
        </strong>{' '}
        You won't be charged until your trial ends.
      </div>
    </div>
  );
}
